import React, { useState, useEffect, useCallback } from 'react';
import { Mail, RefreshCw } from 'lucide-react';
import { auth } from '../firebase';
import { sendVerificationEmail } from '../services/authEmailService';
import { BOOKING_GOLD, BOOKING_BG, CARD_BG, TEXT_MUTED } from '../constants/booking';
import { SECTION_PADDING } from '../styles/layout';

const COOLDOWN_SECONDS = 60;

export default function VerifyEmailGate({ onVerified }) {
  const [cooldown, setCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = async () => {
    const user = auth.currentUser;
    if (!user || cooldown > 0) return;
    try {
      setResendError('');
      setResendMessage('');
      await sendVerificationEmail(user, '/league');
      setResendMessage('Verification email sent from Digital Divot. Check your inbox and spam folder.');
      setCooldown(COOLDOWN_SECONDS);
    } catch (e) {
      const message = e.message || 'Failed to send verification email.';
      setResendError(message);
      if (!message.includes('Too many verification requests')) {
        setCooldown(COOLDOWN_SECONDS);
      }
    }
  };

  const handleRefresh = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      setRefreshing(true);
      setResendError('');
      await user.reload();
      await user.getIdToken(true);
      if (user.emailVerified) {
        onVerified?.();
      } else {
        setResendError('Email not verified yet. Check your inbox and try again.');
      }
    } catch (e) {
      setResendError(e.message || 'Could not refresh verification status.');
    } finally {
      setRefreshing(false);
    }
  }, [onVerified]);

  const email = auth.currentUser?.email;

  return (
    <div
      className="dd-page dd-verify-email"
      style={{
        backgroundColor: BOOKING_BG,
        color: '#F5F0E8',
        minHeight: '60vh',
        padding: SECTION_PADDING,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          maxWidth: '32rem',
          width: '100%',
          backgroundColor: CARD_BG,
          border: '1px solid rgba(184, 151, 42, 0.35)',
          borderRadius: '1rem',
          padding: 'clamp(2rem, 5vw, 3rem)',
          textAlign: 'center',
        }}
      >
        <Mail size={48} color={BOOKING_GOLD} style={{ marginBottom: '1rem' }} />
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.75rem' }}>
          Verify Your Email
        </h1>
        <p style={{ color: TEXT_MUTED, lineHeight: 1.6, marginBottom: '0.5rem' }}>
          We sent a verification link to:
        </p>
        {email && (
          <p style={{ color: BOOKING_GOLD, fontWeight: 600, marginBottom: '1.25rem' }}>{email}</p>
        )}
        <p style={{ color: TEXT_MUTED, fontSize: '0.9375rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
          Verify your email to join The League.
        </p>

        <p style={{ color: TEXT_MUTED, fontSize: '0.8125rem', lineHeight: 1.5, marginBottom: '1.5rem' }}>
          Check your spam or promotions folder. The email comes from Digital Divot, not Firebase.
        </p>

        {resendMessage && (
          <p style={{ color: BOOKING_GOLD, fontSize: '0.875rem', marginBottom: '1rem' }}>{resendMessage}</p>
        )}
        {resendError && (
          <p style={{ color: '#f87171', fontSize: '0.875rem', marginBottom: '1rem' }}>{resendError}</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={handleResend}
            disabled={cooldown > 0}
            style={{
              borderRadius: '9999px',
              backgroundColor: BOOKING_GOLD,
              color: '#000',
              fontWeight: 700,
              padding: '0.75rem 1.5rem',
              border: 'none',
              cursor: cooldown > 0 ? 'not-allowed' : 'pointer',
              opacity: cooldown > 0 ? 0.6 : 1,
            }}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Verification Email'}
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              borderRadius: '9999px',
              backgroundColor: 'transparent',
              color: '#F5F0E8',
              fontWeight: 600,
              padding: '0.75rem 1.5rem',
              border: '1px solid rgba(184, 151, 42, 0.35)',
              cursor: refreshing ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
            }}
          >
            <RefreshCw size={16} />
            {refreshing ? 'Checking…' : "I've verified — refresh"}
          </button>
        </div>
      </div>
    </div>
  );
}
